import { useEffect, useRef, useState } from 'react';

import type { TerrainVisibilityResult } from '../lib/terrainMath';
import type { Coordinate } from '../types';

type TerrainState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; result: TerrainVisibilityResult }
  | { status: 'error' };

interface WorkerResponse {
  requestId: number;
  result?: TerrainVisibilityResult;
  error?: 'UNAVAILABLE';
}

export function useTerrainVisibility(
  location: Coordinate | null,
  sunAltitude: number | null,
  sunAzimuth: number | null,
  enabled: boolean,
): TerrainState {
  const [state, setState] = useState<TerrainState>({ status: 'idle' });
  const nextRequestId = useRef(0);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    if (typeof Worker === 'undefined') {
      return;
    }
    const worker = new Worker(new URL('../workers/terrain.worker.ts', import.meta.url), {
      type: 'module',
    });
    workerRef.current = worker;
    worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      if (event.data.requestId !== nextRequestId.current) return;
      if (event.data.result) setState({ status: 'ready', result: event.data.result });
      else setState({ status: 'error' });
    });
    worker.addEventListener('error', () => {
      setState({ status: 'error' });
      worker.terminate();
      workerRef.current = null;
    });
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const requestId = nextRequestId.current + 1;
    nextRequestId.current = requestId;
    if (!enabled || !location || sunAltitude === null || sunAzimuth === null) {
      setState({ status: 'idle' });
      return;
    }
    const worker = workerRef.current;
    if (!worker) {
      setState({ status: 'error' });
      return;
    }
    setState({ status: 'loading' });
    worker.postMessage({ requestId, location, sunAltitude, sunAzimuth });
  }, [enabled, location, sunAltitude, sunAzimuth]);

  return state;
}
